import { detectLanguageByRegex } from "@services/file-translations/file-translation-validation/language-detection/language-detection-regex";
import { detectLanguageByTrigram } from "@services/file-translations/file-translation-validation/language-detection/language-detection-trigram";

/**
 * Individual language match result
 */
export interface LanguageMatch {
  language: string;      // Language name (e.g., "english")
  languageCode: string;  // Language code (e.g., "en")
  confidence: number;    // Confidence percentage
}

/**
 * Language detection result interface
 */
export interface LanguageResult {
  language: string;              // Top detected language (e.g., "english")
  languageCode: string;          // Top language code (e.g., "en")
  confidence: number;            // Confidence percentage for top language
  method: string;
  matches: LanguageMatch[];      // All detected languages with confidence scores, sorted by confidence
}

/**
 * Character distribution data for a language
 * This interface matches the structure returned by getLanguageDistributions()
 */
export interface LanguageDistribution {
  trigrams: Map<string, number>;  // Top trigrams with normalized frequency
  uniqueChars: Set<string>;       // Characters distinctive to this language
  scriptPrimary: RegExp;          // Primary script regex for quick filtering
  scriptSecondary?: RegExp;       // Optional secondary script for mixed-script languages
}

const errorResult: LanguageResult = {
  language: 'unknown',
  languageCode: 'unknown',
  confidence: 0,
  method: 'none',
  matches: []
};

/**
 * Detects language using a combined approach that prioritizes regex script analysis
 * but uses trigram frequency when it has higher confidence
 * @param text The text content to analyze
 * @returns The detected language information
 */
export function detectLanguage(text: string): LanguageResult {
  errorResult.method = 'none';

  // Handle null or undefined input
  if (text === null || text === undefined) {
    return errorResult;
  }
  // Skip very short texts or texts with no linguistic content
  if (text.length < 10 || !/[a-zA-Z\u00C0-\u00FF\u0100-\uFFFF]/.test(text)) {
    return errorResult;
  }

  // Try both detection methods
  let regexResult = detectTextLanguageByRegex(text);
  const trigramResult = detectTextLanguageByTrigram(text);

  // Determine which detection result to use
  let result: LanguageResult;

  // Get top confidences
  const regexTopConfidence = regexResult.matches.length > 0 ? regexResult.matches[0].confidence : 0;
  const trigramTopConfidence = trigramResult.matches.length > 0 ? trigramResult.matches[0].confidence : 0;
  const maxConfidence = Math.max(regexTopConfidence, trigramTopConfidence);

  if(maxConfidence < 9) {
    return errorResult;
  }

  // If regex confidence is too low
  if (regexTopConfidence <= 8) { //trigramTopConfidence / 2.5
    result = {
      ...trigramResult,
      method: 'trigram'
    };
  }
  // If regex has no useful matches, also fall back to trigram
  else if (regexResult.matches.length === 0 || regexTopConfidence === 0) {
    result = {
      ...trigramResult,
      method: 'trigram'
    };
  } else {
    // Find all matches with the same top confidence (ties)
    const topTies = regexResult.matches.filter(match => match.confidence === regexTopConfidence);

    // If there's only one top match (no ties)
    if (topTies.length === 1) {
      result = {
        ...regexResult,
        method: 'regex'
      };
    } else {
      // We have ties, try to break them with trigram analysis
      const overlappingLanguages = topTies.filter(regexMatch =>
        trigramResult.matches.some(trigramMatch =>
          trigramMatch.language === regexMatch.language
        )
      );

      if (overlappingLanguages.length > 0) {
        // Sort overlapping languages by trigram confidence
        overlappingLanguages.sort((a, b) => {
          const aTrigramMatch = trigramResult.matches.find(m => m.language === a.language);
          const bTrigramMatch = trigramResult.matches.find(m => m.language === b.language);
          return (bTrigramMatch?.confidence || 0) - (aTrigramMatch?.confidence || 0);
        });

        // Use top language from overlap
        result = {
          language: overlappingLanguages[0].language,
          languageCode: overlappingLanguages[0].languageCode,
          confidence: overlappingLanguages[0].confidence,
          method: 'regex+trigram',
          matches: regexResult.matches
        };
      } else {

        result = {
          language: topTies[0].language,
          languageCode: topTies[0].languageCode,
          confidence: topTies[0].confidence,
          method: 'regex',
          matches: regexResult.matches
        };
      }
    }
  }

  if (result.language === 'luxembourgish' || result.language === 'kinyarwanda') {
    return result;
  }
  if ((result.language === 'finnish' || result.language === 'german') && trigramResult.confidence >= regexResult.confidence) {
    result = {
      ...trigramResult,
      method: 'trigram'
    };
  }
  if ( result.language === 'dutch' && ((trigramResult.language === 'turkish') && trigramResult.confidence >= regexResult.confidence)) {
    result = {
      ...trigramResult,
      method: 'trigram'
    };
  }

  if ( result.language === 'galician' && ( trigramResult.language != 'spanish') ) {
    result = {
      ...trigramResult,
      method: 'trigram'
    };
  }
  else if (result.language === 'spanish' || result.language === 'portuguese' || result.language === 'galician' || result.language === 'catalan') {
    result = determineIberianLanguage(text, result, regexResult, trigramResult);
  } else if ( regexTopConfidence < 10 && trigramTopConfidence >= 10) {
    result = {
      ...trigramResult,
      method: 'trigram'
    };
  }

  // Check for regional variants as the final step
  const language = result.language;
  if (language === 'chinese' || language === 'french' || language === 'occitan' || language == 'scottishGaelic') {
    result = checkLanguageRegion(result, text);
  }
  return result;
}


/**
 * Detects the language of text content using trigram frequency analysis
 * @param text The text content to analyze
 * @returns The detected language information with all matches
 */
function detectTextLanguageByTrigram(text: string): LanguageResult {
  // Use the imported function
  const trigramResult = detectLanguageByTrigram(text);

  // Convert the result format to match the expected LanguageResult interface
  const matches: LanguageMatch[] = trigramResult.allMatches.map((match: { language: any; languageCode: any; confidence: any; }) => ({
    language: match.language,
    languageCode: match.languageCode,
    confidence: match.confidence
  }));

  // If no matches were found
  errorResult.method = 'trigram';
  if (matches.length === 0) {
    return errorResult;
  }

  // Get the primary language match
  const primaryMatch = trigramResult.primaryLanguage;

  return {
    language: primaryMatch.language,
    languageCode: primaryMatch.languageCode,
    confidence: primaryMatch.confidence,
    method: 'trigram',
    matches: matches
  };
}

/**
 * Detects the language of text content using regex pattern matching
 * @param text The text content to analyze
 * @returns The detected language information with all matches
 */
function detectTextLanguageByRegex(text: string): LanguageResult {
  // Use the existing function from language-detection-regex.ts
  const regexResult = detectLanguageByRegex(text);

  // Convert the result format to match the expected LanguageResult interface
  const matches: LanguageMatch[] = regexResult.allMatches.map(match => ({
    language: match.language,
    languageCode: match.languageCode,
    confidence: match.confidence
  }));

  // If no matches were found
  errorResult.method = 'regex';
  if (matches.length === 0) {
    return errorResult;
  }

  // Get the primary language match
  const primaryMatch = regexResult.primaryLanguage;

  return {
    language: primaryMatch.language,
    languageCode: primaryMatch.languageCode,
    confidence: primaryMatch.confidence,
    method: 'regex',
    matches: matches
  };
}

function checkLanguageRegion(result: LanguageResult, text: string): LanguageResult {
  if (result.language === 'chinese') {
    return detectChineseRegion(text, result);
  } else if (result.language === 'french') {
    return detectFrenchRegion(text, result);
  } else if (result.language === 'occitan') {
    return { ...result, language: 'catalan', languageCode: 'ca' };
  } else if (result.language === 'scottishGaelic') {
    return { ...result, language: 'irish', languageCode: 'ga' };
  }

  return result;
}

function detectChineseRegion(text: string, result: LanguageResult): LanguageResult {
  const simplifiedOnly = /[国识发课习约还这现务产业]/g;
  const traditionalOnly = /[國識發課習約還這現務產業]/g;

  const simplifiedCount = (text.match(simplifiedOnly) || []).length;
  const traditionalCount = (text.match(traditionalOnly) || []).length;

  if (traditionalCount > simplifiedCount) {
    return { ...result, language: 'chinese_traditional', languageCode: 'zh-Hant' };
  } else {
    return { ...result, language: 'chinese_simplified', languageCode: 'zh-Hans' };
  }
}

function detectFrenchRegion(text: string, result: LanguageResult): LanguageResult {
  const canadianTerms = /\b(?:chum|blonde|char|magasiner|breuvage|dépanneur|pogner|fin de semaine|présentement|canceller|céduler|bienvenue|souper|chandail|niaiseux|tuque)\b/gi;

  const franceFrenchTerms = /\b(?:caddie|goûter|petit-déjeuner|putain|mec|meuf|bagnole|resto|métro|apéro|sympa|vachement|lycée|truc|kiffer|bouffer|bosser|boulot)\b/gi;

  const canadianMatches = (text.match(canadianTerms) || []).length;
  const franceMatches = (text.match(franceFrenchTerms) || []).length;

  if (canadianMatches > franceMatches) {
    return { ...result, language: 'french_canada', languageCode: 'fr-CA' };
  } else if (franceMatches > 0) {
    return { ...result, language: 'french', languageCode: 'fr' };
  } else {
    return { ...result, language: 'french', languageCode: 'fr' };
  }
}

function determineIberianLanguage(text: string, result: LanguageResult, regexResult: LanguageResult, trigramResults: LanguageResult): LanguageResult {
  // First filter matches to only include Iberian languages from both trigram and regex results
  const iberianTrigramMatches = trigramResults.matches.filter(match =>
    ['spanish', 'spanish_latin_america', 'spanish_spain', 'portuguese', 'galician', 'catalan'].includes(match.language)
  );

  const iberianRegexMatches = regexResult.matches.filter(match =>
    ['spanish', 'spanish_latin_america', 'spanish_spain', 'portuguese', 'galician', 'catalan'].includes(match.language)
  );

  if (iberianTrigramMatches.length === 0 && iberianRegexMatches.length === 0) {
    return result;
  }

  // Create simplified scores based on both trigram and regex matches
  let languageScores = {
    portuguese: 0,
    galician: 0,
    catalan: 0,
    spanish: 0
  };

  // Add scores from trigram analysis (base weight: 1.0)
  for (const match of iberianTrigramMatches) {
    if (match.language === 'portuguese' || match.language === 'portuguese_portugal') {
      languageScores.portuguese += match.confidence;
    }
    else if (match.language === 'galician') {
      languageScores.galician += match.confidence;
    }
    else if (match.language === 'catalan') {
      languageScores.catalan += match.confidence;
    }
    else if (match.language.startsWith('spanish')) {
      languageScores.spanish += match.confidence;
    }
  }

  // Add scores from regex analysis (base weight: 1.2 - slightly higher than trigram)
  for (const match of iberianRegexMatches) {
    if (match.language === 'portuguese' || match.language === 'portuguese_portugal') {
      languageScores.portuguese += match.confidence * 1.2;
    }
    else if (match.language === 'galician') {
      languageScores.galician += match.confidence * 1.2;
    }
    else if (match.language === 'catalan') {
      languageScores.catalan += match.confidence * 1.2;
    }
    else if (match.language.startsWith('spanish')) {
      languageScores.spanish += match.confidence * 1.2;
    }
  }

  // Add scores from distinctive features
  // Portuguese distinctive features
  const portugueseMarkers = /\b(não|são|ção|ções|muito|também|uma|um|isto|isso|aquilo|quem|quando|onde|como|porquê|porque)\b|[ãõ]/gi;
  const portugueseCount = (text.match(portugueseMarkers) || []).length;
  languageScores.portuguese += portugueseCount * 2.5; // Increased weight

  // Galician distinctive features
  const galicianMarkers = /\b(unha|máis|tamén|vós|moito|quen|cando|onde|como|por que|porque|ao|ó|nh)\b/gi;
  const galicianCount = (text.match(galicianMarkers) || []).length;
  languageScores.galician += galicianCount * 2.5; // Increased weight

  // Catalan distinctive features
  const catalanMarkers = /\b(això|també|més|són|nosaltres|vosaltres|qui|quan|on|com|per què|perquè|amb|ny)\b|[àèòïü]/gi;
  const catalanCount = (text.match(catalanMarkers) || []).length;
  languageScores.catalan += catalanCount * 2.5; // Increased weight

  // Spanish distinctive features
  const spanishMarkers = /\b(el|las|los|muy|este|esta|estos|estas|nuestro|vuestro|quién|cuándo|dónde|cómo|por qué|porque|yo|tú)\b|[ñ¿¡]/gi;
  const spanishCount = (text.match(spanishMarkers) || []).length;
  languageScores.spanish += spanishCount * 2.5; // Increased weight

  // Adjust scores based on negative markers (patterns that shouldn't appear in a language)
  // Spanish negative markers (patterns that shouldn't appear in Spanish)
  const spanishNegativeMarkers = /\b(não|são|uma|um|nh|ny|amb|això|ó|ao)\b|[ãõàèò]/gi;
  const spanishNegativeCount = (text.match(spanishNegativeMarkers) || []).length;
  languageScores.spanish -= spanishNegativeCount * 1.5;

  // Portuguese negative markers
  const portugueseNegativeMarkers = /\b(muy|este|nuestro|vuestro|lo|las|donde|cuando|ñ)\b|[¿¡]/gi;
  const portugueseNegativeCount = (text.match(portugueseNegativeMarkers) || []).length;
  languageScores.portuguese -= portugueseNegativeCount * 1.5;

  // Galician negative markers
  const galicianNegativeMarkers = /\b(muy|este|amb|això|són|nosaltres|vosaltres)\b|[àèòïü¿¡]/gi;
  const galicianNegativeCount = (text.match(galicianNegativeMarkers) || []).length;
  languageScores.galician -= galicianNegativeCount * 1.5;

  // Catalan negative markers
  const catalanNegativeMarkers = /\b(muy|este|não|são|uma|nh|ó|ao)\b|[ãõñ¿¡]/gi;
  const catalanNegativeCount = (text.match(catalanNegativeMarkers) || []).length;
  languageScores.catalan -= catalanNegativeCount * 1.5;

  // Get the language with the highest score
  let topLanguage = '';
  let topScore = 0;

  for (const [language, score] of Object.entries(languageScores)) {
    if (score > topScore) {
      topScore = score;
      topLanguage = language;
    }
  }

  // Map to appropriate language code and variant
  if (topLanguage === 'portuguese') {
    return { ...result, language: 'portuguese_portugal', languageCode: 'pt-PT', method: 'iberian-combined' };
  }
  else if (topLanguage === 'spanish') {
    // Differentiate between Spanish variants if needed
    if (text.match(/\b(vosotros|vuestro|coño|joder|vale|tío|chaval|guay|coche|móvil|ordenador|gafas)\b/gi)) {
      return { ...result, language: 'spanish_spain', languageCode: 'es-ES', method: 'iberian-combined' };
    } else {
      return { ...result, language: 'spanish_latin_america', languageCode: 'es-419', method: 'iberian-combined' };
    }
  }
  else if (topLanguage === 'galician') {
    return { ...result, language: 'galician', languageCode: 'gl', method: 'iberian-combined' };
  }
  else if (topLanguage === 'catalan') {
    return { ...result, language: 'catalan', languageCode: 'ca', method: 'iberian-combined' };
  }

  // Fallback to original result if something unexpected happens
  return result;
}
